/**
 * Input validation and sanitization utilities
 */

// HTML entities to prevent XSS
const HTML_ENTITIES: Record<string, string> = {
  '&': '&amp;',
  '<': '&lt;',
  '>': '&gt;',
  '"': '&quot;',
  "'": '&#x27;',
  '/': '&#x2F;',
};

/**
 * Escape HTML entities in a string
 */
export const escapeHtml = (str: string): string => {
  return str.replace(/[&<>"'/]/g, (match) => HTML_ENTITIES[match] || match);
};

/**
 * Sanitize user input for search queries
 */
export const sanitizeSearchInput = (input: string): string => {
  if (typeof input !== 'string') {
    return '';
  }
  
  return input
    .trim()
    .replace(/\s+/g, ' ') // Normalize whitespace
    .replace(/[<>]/g, '') // Remove potential HTML tags
    .substring(0, 100); // Limit length
};

/**
 * Validate search query
 */
export const validateSearchQuery = (query: string): { isValid: boolean; error?: string } => {
  if (!query || typeof query !== 'string') {
    return { isValid: false, error: 'Search query must be a non-empty string' };
  }
  
  const sanitized = sanitizeSearchInput(query);
  
  if (sanitized.length < 1) {
    return { isValid: false, error: 'Search query cannot be empty' };
  }
  
  if (sanitized.length > 100) {
    return { isValid: false, error: 'Search query is too long (maximum 100 characters)' };
  }
  
  // Check for suspicious patterns
  const suspiciousPatterns = [
    /<script/i,
    /javascript:/i,
    /data:/i,
    /vbscript:/i,
    /onload=/i,
    /onclick=/i,
  ];
  
  for (const pattern of suspiciousPatterns) {
    if (pattern.test(query)) {
      return { isValid: false, error: 'Invalid characters in search query' };
    }
  }
  
  return { isValid: true };
};

/**
 * Check if string contains Chinese characters
 */
export const containsChinese = (text: string): boolean => {
  return /[\u4e00-\u9fff]/.test(text);
};

/**
 * Detect language based on text content
 */
export const detectLanguage = (text: string): 'en' | 'zh' => {
  return containsChinese(text) ? 'zh' : 'en';
};

/**
 * Validate coordinates
 */
export const validateCoordinates = (lat: number, lng: number): boolean => {
  return (
    typeof lat === 'number' &&
    typeof lng === 'number' &&
    lat >= -90 &&
    lat <= 90 &&
    lng >= -180 &&
    lng <= 180 &&
    !isNaN(lat) &&
    !isNaN(lng)
  );
};

/**
 * Check if text is likely an address
 */
export const isLikelyAddress = (text: string): boolean => {
  const addressPatterns = [
    /\d+.*(?:street|road|avenue|drive|lane|way|court|place)/i,
    /\d+.*(?:街|路|道|里|巷|弄)/,
    /(?:building|tower|centre|center|plaza|mall).*\d+/i,
    /(?:大廈|大厦|中心|廣場|商場).*\d+/,
  ];
  
  return addressPatterns.some(pattern => pattern.test(text));
};
